import React, { useState } from "react";
import Pagination from "../../../components/ui/Pagination";
import useModal from "../../../hooks/ui/useModal";
import Modal from "../../../components/ui/utils/Modal";
import ViewOrder from "../../../components/user/order/ViewUserOrder";
import { FaEye, FaRupeeSign, FaCalendarAlt, FaStar } from "react-icons/fa";
import { HiStatusOnline } from "react-icons/hi";
import ConfirmOrder from "./ConfirmOrder";
import ProductDetails from "./ProductDetails";
import { formatDate } from "../../../utils/helper";
import ViewOrderQRCode from "./ViewOrderQRCode";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import toast from "react-hot-toast";

export const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Lead Requested":
      return "bg-blue-100 text-blue-700";
    case "Dispatched":
      return "bg-emerald-100 text-emerald-700";
    case "Cancelled":
      return "bg-red-100 text-red-700";
    case "Completed":
      return "bg-green-100 text-green-700";
    case "Requested to Cancel":
      return "bg-yellow-100 text-yellow-800";
    case "Rejected Cancellation":
      return "bg-orange-100 text-orange-800";
    case "Processing":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const OrderList = ({
  currentPage,
  data,
  error,
  limit,
  loading,
  setPage,
  setSorting,
  sort,
  totalItems,
  fetchData,
  totalPages,
}) => {
  const { isOpen, openModal, closeModal, actionType } = useModal();

  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };

  
  return (
    <>
      <div className="md:p-6 bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between pb-6">
          <h1 className="text-2xl font-bold text-gray-800">My Orders</h1>
        </div>

        {/* ERROR / EMPTY */}
        {error && (
          <p className="text-center text-red-500 text-sm mb-3">
            {error?.message || "Failed to load orders"}
          </p>
        )}

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div
                key={idx}
                className="h-32 rounded-xl bg-gray-100 animate-pulse"
              ></div>
            ))}
          </div>
        ) : (data?.length ?? 0) === 0 ? (
          <p className="text-center text-gray-500 py-6">No orders found</p>
        ) : (
          <div className="space-y-4">
            {data?.map((order) => {
              const lastStatus =
                order?.reports?.[order?.reports?.length - 1]?.status || "";

              const firstProduct = order?.products?.[0];
              const productName =
                firstProduct?.product?.name || "Order Item";

              return (
                <div
                  key={order._id}
                  className="border bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="font-bold text-sm text-gray-900">
                        {order?.orderId || order?._id}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {order?.vendorManagerId?.name || "GK Contactless Ordering"}
                      <span className="text-xs px-2 bg-green-50 rounded-lg mx-1 text-gray-500 mt-1">
                        {order?.vendorManagerId?.corporateCodeId[0]?.corporateCode || "GK Contactless Ordering"}
                      </span>
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className={`flex items-center gap-1 px-3 py-1 text-xs rounded-full font-semibold ${getStatusBadgeClass(
                          lastStatus
                        )}`}
                      >
                        <HiStatusOnline className="text-xs" />
                       Order {order.orderStatus || "Requested"}
                      </span>

                      <span className="px-3 py-1 bg-yellow-300 rounded-full font-bold text-xs">
                        {order?.tokenNo?.toString().padStart(4, "0") || "0005"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 gap-3">
                    <div className="flex-1">
                      <p className="text-sm flex items-center gap-2 text-gray-800">
                        <span className="w-2 h-2 bg-red-500 rounded-full inline-block"></span>
                        {firstProduct?.quantity || 1} x {productName}
                      </p>
                    </div>

                    <div className="flex items-center gap-5">
                      {order?.qrImageUrl || order?.qrCodeUrl ? (
                        <img
                          src={order?.qrImageUrl || order?.qrCodeUrl}
                          alt="QR Code"
                          className="w-12 h-12 object-contain border rounded"
                        />
                      ) : (
                        <button
                          onClick={() =>
                            openModal({
                              type: "viewQRCode",
                              data: { state: order },
                            })
                          }
                          className="text-xs px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-100"
                        >
                          View QR
                        </button>
                      )}

                      <p className="flex items-center gap-1 font-bold text-gray-900 text-base">
                        <FaRupeeSign size={14} />
                        {order.totalAmount.toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 mt-2">
                    {formatDate(order.createdAt)} at{" "}
                    {new Date(order.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>

                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() =>
                        openModal({
                          type: "view",
                          data: { state: order },
                        })
                      }
                      className="flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-100"
                    >
                      <FaEye size={12} />
                      View
                    </button>

                    {order?.reports?.some(
                      (report) => report.status === "Dispatched"
                    ) &&
                      order.products?.some(
                        (product) => product.isConfirmed !== true
                      ) && (
                        <button
                          onClick={() =>
                            openModal({
                              type: "confirm",
                              data: { state: order },
                            })
                          }
                          className="text-xs px-3 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          Confirm
                        </button>
                      )}

                    {/* <button
                      onClick={() =>
                        openModal({
                          type: "products",
                          data: { state: order },
                        })
                      }
                      className="text-xs px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-100"
                    >
                      Products
                    </button> */}
                  </div>

                 
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-sm hidden md:flex font-semibold text-red-600">Rate</span>

                    {[1, 2, 3, 4, 5].map((star) => {
                      const isRated = order?.orderRating >= star;
                      const ratingGiven = !!order?.orderRating; // true if rating already submitted

                      return (
                        <button
                          key={star}
                          disabled={ratingGiven} 
                          onClick={() =>
                            openModal({
                              type: "feedback",
                              data: { state: order, rating: star },
                            })
                          }
                          className={`flex items-center gap-1 text-xs px-3 py-1 rounded-md border transition
          ${isRated ? "border-yellow-500 bg-yellow-50" : "border-gray-300"}
          ${ratingGiven ? "cursor-not-allowed opacity-60" : "hover:bg-gray-100"}
        `}
                        >
                          <span className={isRated ? "text-yellow-600" : "text-gray-500"}>
                            {star}
                          </span>
                          <FaStar size={12} className={isRated ? "text-yellow-500" : "text-gray-400"} />
                        </button>
                      );
                    })}
                  </div>

                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              pagination={{
                currentPage,
                totalPages,
                totalItems,
                perPage: limit,
              }}
              handlePageChange={setPage}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* MODALS */}
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        size={actionType?.type === "products" ? "extralarge" : "lg"}
        overflow={"overflow-y-scroll"}
      >
        {actionType?.type === "view" && (
          <ViewOrder order={actionType?.data?.state} closeModal={closeModal} />
        )}

        {actionType?.type === "viewQRCode" && (
          <ViewOrderQRCode
            order={actionType?.data?.state}
            closeModal={closeModal}
          />
        )}

        {actionType?.type === "confirm" && (
          <ConfirmOrder
            order={actionType?.data?.state}
            closeModal={closeModal}
            fetchData={fetchData}
          />
        )}

        {actionType?.type === "products" && (
          <ProductDetails
            order={actionType?.data?.state}
            closeModal={closeModal}
          />
        )}

        {actionType?.type === "feedback" && (
          <OrderFeedback
            order={actionType?.data?.state}
            initialRating={actionType?.data?.rating}
            closeModal={closeModal}
            fetchData={fetchData}
          />
        )}
      </Modal>
    </>
  );
};

export default OrderList;

/* ================= FEEDBACK MODAL ================= */

const OrderFeedback = ({ order, initialRating = 0, closeModal, fetchData }) => {
  const [rating, setRating] = useState(initialRating);
  const [remark, setRemark] = useState("");
  const { post, isLoading } = usePost(apiRequest.submitOrderRating);

  const handleSubmit = async () => {
    if (!rating) {
      return toast.error("Please select a rating");
    }

    try {
      const res = await post({
        data: {
          body: {
            orderId: order._id,
            rating,
            remark,
          },
        },
      });

      if (res?.response) {
        toast.success(res?.message || "Thank you for your feedback!");
        if (typeof fetchData === "function") {
          fetchData();
        }
        closeModal();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to submit rating. Try again."
      );
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-2">Rate Your Order</h2>
      <p className="text-xs text-gray-500 mb-4">
        {order?.orderId} • {formatDate(order?.createdAt)}
      </p>

      <div className="flex gap-2 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            className={`flex items-center justify-center w-10 h-10 rounded-md border text-sm ${rating >= star
                ? "bg-yellow-400 text-white border-yellow-500"
                : "bg-white text-gray-600 border-gray-300"
              }`}
          >
            {star}
            <FaStar className="ml-1" size={10} />
          </button>
        ))}
      </div>

      <textarea
        rows={4}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        className="w-full border rounded-md p-2 text-sm mb-3"
        placeholder="Write your feedback..."
      />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={closeModal}
          className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isLoading}
          onClick={handleSubmit}
          className="px-4 py-2 text-sm rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-60"
        >
          {isLoading ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
};
